import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  User,
  Sun,
  Moon,
  Monitor,
  Check,
  Crown,
  Key,
  Copy,
  Trash2,
  Shield,
  Loader2,
  CheckCircle2,
  X,
  CreditCard,
  Pencil,
  ChevronDown,
  Palette,
  Bell,
} from 'lucide-react';
import { Button } from '../components/common/Button';
import { useThemeStore, useAuthStore } from '../store';
import {
  redirectToCheckout,
  redirectToPortal,
  changeSubscription,
  cancelPendingDowngrade,
  previewChange,
  createUpgradeCheckout,
  completeUpgrade,
  isTierUpgrade,
  isTierDowngrade,
  SUBSCRIPTION_TIERS,
} from '../utils/stripe';
import { getApiKeys, createApiKey, revokeApiKey, type ApiKeyData } from '../utils/api';
import type { Theme, SubscriptionTierType } from '../types';

// ============================================================================
// CONSTANTS
// ============================================================================

const TABS = [
  { id: 'general', label: 'General', icon: User, enterpriseOnly: false },
  { id: 'plans', label: 'Plans', icon: CreditCard, enterpriseOnly: false },
  { id: 'security', label: 'Security', icon: Shield, enterpriseOnly: false },
  { id: 'api', label: 'API Keys', icon: Key, enterpriseOnly: true },
] as const;

type TabId = (typeof TABS)[number]['id'];

const THEME_OPTIONS: { value: Theme; label: string; icon: typeof Sun }[] = [
  { value: 'system', label: 'System', icon: Monitor },
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
];

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    monthlyPrice: '$0',
    period: '/forever',
    features: ['5 stocks tracked', 'Basic sentiment analysis', '7 days of history', 'Daily updates'],
  },
  {
    id: 'starter',
    name: 'Starter',
    monthlyPrice: '$49',
    yearlyPrice: '$399',
    period: '/month',
    features: ['25 tracked stocks', '30 days of history', 'AI Chat assistant', 'AI sentiment summaries', 'Email alerts'],
  },
  {
    id: 'pro',
    name: 'Pro',
    monthlyPrice: '$99',
    yearlyPrice: '$799',
    period: '/month',
    features: ['100 tracked stocks', '1 year of history', 'AI Chat assistant', 'Backtesting simulator', 'Performance analytics', 'Data export'],
  },
  {
    id: 'early_supporter',
    name: 'Early Supporter',
    monthlyPrice: '$199',
    period: ' one-time',
    features: ['Lifetime access', '100 tracked stocks', '1 year of history', 'AI Chat assistant', 'Backtesting simulator', 'All future Pro features'],
    popular: true,
    badge: 'Limited Time',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    monthlyPrice: 'Custom',
    period: '',
    features: ['Unlimited tracked stocks', 'Full API access', 'Dedicated support', 'Custom integrations'],
    isEnterprise: true,
  },
];

// ============================================================================
// COMPONENTS
// ============================================================================

function Toast({ message, type = 'success', onClose }: { message: string; type?: 'success' | 'error'; onClose: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 2500);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={clsx(
      'fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 z-50 flex items-center justify-center sm:justify-start gap-2 px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium',
      type === 'success' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
    )}>
      {type === 'success' ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <X className="h-4 w-4 shrink-0" />}
      {message}
    </div>
  );
}

function DeleteAccountModal({ isOpen, onConfirm, onCancel }: {
  isOpen: boolean; onConfirm: () => void; onCancel: () => void;
}) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (!password.trim()) {
      setError('Please enter your password');
      return;
    }
    // TODO: Validate password with backend
    onConfirm();
  };

  const handleClose = () => {
    setPassword('');
    setError('');
    onCancel();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative bg-[var(--color-bg-primary)] rounded-xl p-5 sm:p-6 max-w-sm w-full border border-[var(--color-border)]">
        <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">Delete Account</h3>
        <p className="mt-2 text-sm text-[var(--color-text-muted)]">
          This will permanently delete your account and all data. This action cannot be undone.
        </p>
        <div className="mt-4">
          <label className="block text-xs text-[var(--color-text-muted)] mb-1.5">Enter your password to confirm</label>
          <input
            type="password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(''); }}
            placeholder="Password"
            className="w-full px-3 py-2.5 rounded-lg text-sm bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:ring-1 focus:ring-red-500"
          />
          {error && <p className="mt-1.5 text-xs text-red-500">{error}</p>}
        </div>
        <div className="mt-5 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 sm:justify-end">
          <Button variant="secondary" size="sm" onClick={handleClose} className="w-full sm:w-auto">Cancel</Button>
          <Button variant="danger" size="sm" onClick={handleSubmit} className="w-full sm:w-auto">Delete Account</Button>
        </div>
      </div>
    </div>
  );
}

function Toggle({ checked, onChange, disabled = false }: { checked: boolean; onChange: () => void; disabled?: boolean }) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      disabled={disabled}
      className={clsx(
        'relative w-10 h-5 rounded-full transition-colors',
        checked ? 'bg-[var(--color-accent)]' : 'bg-[var(--color-bg-tertiary)]',
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
      )}
    >
      <span className={clsx(
        'absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform',
        checked ? 'left-5' : 'left-0.5'
      )} />
    </button>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <p className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wide mb-3">{children}</p>;
}

function CollapsibleSection({
  title,
  icon: Icon,
  defaultOpen = true,
  children,
  headerRight,
}: {
  title: string;
  icon: typeof User;
  defaultOpen?: boolean;
  children: React.ReactNode;
  headerRight?: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="rounded-xl bg-[var(--color-bg-primary)] border border-[var(--color-border)] overflow-hidden">
      {/* Header - always visible */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 sm:p-5 cursor-pointer hover:bg-[var(--color-bg-secondary)]/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[var(--color-accent)]/10 flex items-center justify-center shrink-0">
            <Icon className="h-4 w-4 text-[var(--color-accent)]" />
          </div>
          <span className="text-sm font-semibold text-[var(--color-text-primary)]">{title}</span>
        </div>
        <div className="flex items-center gap-2">
          {headerRight}
          <ChevronDown
            className={clsx(
              'h-5 w-5 text-[var(--color-text-muted)] transition-transform duration-200',
              isOpen && 'rotate-180'
            )}
          />
        </div>
      </button>

      {/* Collapsible content */}
      <div
        className={clsx(
          'overflow-hidden transition-all duration-200 ease-in-out',
          isOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
        )}
      >
        <div className="px-4 sm:px-5 pb-4 sm:pb-5 pt-0">
          {children}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// TAB: GENERAL
// ============================================================================

function GeneralTab({
  user,
  theme,
  setTheme,
  notifications,
  setNotifications,
  isPro,
  showToast,
}: any) {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');

  const handleSave = () => {
    showToast('Profile updated');
    setIsEditing(false);
  };

  const handleCancel = () => {
    setName(user?.name || '');
    setEmail(user?.email || '');
    setIsEditing(false);
  };

  return (
    <div className="space-y-4">
      {/* Profile */}
      <CollapsibleSection
        title="Profile"
        icon={User}
        defaultOpen={true}
        headerRight={
          !isEditing && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsEditing(true);
              }}
              className="flex items-center gap-1.5 text-sm text-[var(--color-accent)] hover:underline cursor-pointer"
            >
              <Pencil className="h-3.5 w-3.5" />
              Edit
            </button>
          )
        }
      >
        {isEditing ? (
          <div className="space-y-4">
            <div>
              <label className="block text-xs text-[var(--color-text-muted)] mb-1.5">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                autoFocus
                className="w-full px-3 py-2.5 rounded-lg text-sm bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:ring-1 focus:ring-[var(--color-accent)]"
              />
            </div>
            <div>
              <label className="block text-xs text-[var(--color-text-muted)] mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full px-3 py-2.5 rounded-lg text-sm bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:ring-1 focus:ring-[var(--color-accent)]"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={handleSave}>Save</Button>
              <Button variant="secondary" size="sm" onClick={handleCancel}>Cancel</Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-x-8 gap-y-2">
            <div>
              <p className="text-[10px] text-[var(--color-text-muted)] uppercase tracking-wide">Name</p>
              <p className="text-sm text-[var(--color-text-primary)] font-medium">{name || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] text-[var(--color-text-muted)] uppercase tracking-wide">Email</p>
              <p className="text-sm text-[var(--color-text-primary)] font-medium">{email || '—'}</p>
            </div>
          </div>
        )}
      </CollapsibleSection>

      {/* Theme */}
      <CollapsibleSection title="Theme" icon={Palette} defaultOpen={true}>
        <div className="flex flex-wrap gap-2">
          {THEME_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setTheme(opt.value)}
              className={clsx(
                'flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg border text-sm font-medium transition-all cursor-pointer',
                theme === opt.value
                  ? 'border-[var(--color-accent)] bg-[var(--color-accent)]/5 text-[var(--color-accent)]'
                  : 'border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-text-muted)]'
              )}
            >
              <opt.icon className="h-4 w-4" />
              {opt.label}
            </button>
          ))}
        </div>
      </CollapsibleSection>

      {/* Notifications */}
      <CollapsibleSection title="Notifications" icon={Bell} defaultOpen={true}>
        <div className="space-y-1">
          {[
            { key: 'email', label: 'Email notifications', proOnly: false },
            { key: 'push', label: 'Push notifications', proOnly: true },
            { key: 'signals', label: 'Signal alerts', proOnly: true },
            { key: 'weekly', label: 'Weekly digest', proOnly: false },
          ].map((item) => {
            const disabled = item.proOnly && !isPro;
            return (
              <div
                key={item.key}
                className={clsx(
                  'flex items-center justify-between p-3 rounded-lg',
                  disabled ? 'opacity-50' : 'hover:bg-[var(--color-bg-secondary)] cursor-pointer'
                )}
                onClick={() => !disabled && setNotifications((prev: any) => ({ ...prev, [item.key]: !prev[item.key] }))}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[var(--color-text-primary)]">{item.label}</span>
                  {item.proOnly && !isPro && (
                    <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-amber-500 text-white">PRO</span>
                  )}
                </div>
                <Toggle checked={notifications[item.key]} onChange={() => {}} disabled={disabled} />
              </div>
            );
          })}
        </div>
      </CollapsibleSection>
    </div>
  );
}

// ============================================================================
// TAB: PLANS
// ============================================================================

function ConfirmPlanChangeModal({
  isOpen,
  planId,
  planName,
  isUpgrade,
  effectiveDate,
  currentPlanName,
  billingPeriod,
  loading,
  onConfirm,
  onCancel,
}: {
  isOpen: boolean;
  planId: string;
  planName: string;
  isUpgrade: boolean;
  effectiveDate?: string;
  currentPlanName: string;
  billingPeriod: 'monthly' | 'yearly';
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [preview, setPreview] = useState<{
    amount_due: number;
    currency: string;
    days_remaining: number;
  } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !isUpgrade || !planId) {
      setPreview(null);
      setPreviewError(null);
      return;
    }

    setPreviewLoading(true);
    setPreviewError(null);
    previewChange(planId, billingPeriod)
      .then((data) => {
        setPreview({
          amount_due: data.amount_due,
          currency: data.currency,
          days_remaining: data.days_remaining,
        });
      })
      .catch((err) => {
        setPreviewError(err.message || 'Failed to load pricing');
      })
      .finally(() => setPreviewLoading(false));
  }, [isOpen, isUpgrade, planId, billingPeriod]);

  if (!isOpen) return null;

  const formatAmount = (cents: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(cents / 100);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative bg-[var(--color-bg-primary)] rounded-xl p-5 sm:p-6 max-w-sm w-full border border-[var(--color-border)]">
        <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">
          {isUpgrade ? 'Upgrade' : 'Downgrade'} to {planName}?
        </h3>

        {isUpgrade ? (
          <div className="mt-3">
            {previewLoading ? (
              <div className="flex items-center gap-2 text-sm text-[var(--color-text-muted)] py-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Calculating prorated price...
              </div>
            ) : previewError ? (
              <p className="text-sm text-red-500 py-2">{previewError}</p>
            ) : preview ? (
              <div className="space-y-2">
                <div className="p-3 rounded-lg bg-[var(--color-accent)]/5 border border-[var(--color-accent)]/20">
                  <p className="text-2xl font-bold text-[var(--color-text-primary)]">
                    {formatAmount(preview.amount_due, preview.currency)}
                  </p>
                  <p className="text-xs text-[var(--color-text-muted)] mt-1">
                    Prorated for {preview.days_remaining} day{preview.days_remaining !== 1 ? 's' : ''} remaining in your billing cycle
                  </p>
                </div>
                <p className="text-sm text-[var(--color-text-muted)]">
                  Your plan will upgrade to {planName} immediately. Your card on file will be charged.
                </p>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="mt-3 space-y-4">
            <div className="bg-[var(--color-bg-secondary)] rounded-xl p-4 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-[var(--color-text-muted)]">Current plan</span>
                <span className="text-[var(--color-text-primary)] font-medium">{currentPlanName}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[var(--color-text-muted)]">Switching to</span>
                <span className="text-[var(--color-text-primary)] font-medium">{planName}</span>
              </div>
              <div className="border-t border-[var(--color-border)]" />
              <div className="flex justify-between text-sm">
                <span className="text-[var(--color-text-muted)]">{currentPlanName} access until</span>
                <span className="text-[var(--color-text-primary)] font-medium">{effectiveDate || 'end of billing period'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[var(--color-text-muted)]">{planName} starts</span>
                <span className="text-[var(--color-text-primary)] font-medium">{effectiveDate || 'next billing cycle'}</span>
              </div>
            </div>
            <p className="text-sm text-[var(--color-text-muted)]">
              You'll keep all {currentPlanName} features until {effectiveDate ? <strong>{effectiveDate}</strong> : 'the end of your billing period'}. No additional charges until then.
            </p>
          </div>
        )}

        <div className="mt-5 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 sm:justify-end">
          <Button variant="secondary" onClick={onCancel} className="w-full sm:w-auto py-2.5 text-base" disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            className={clsx(
              'w-full sm:w-auto flex items-center justify-center gap-2 py-2.5 text-base font-semibold',
              !isUpgrade && 'bg-orange-500 hover:bg-orange-600 border-orange-500'
            )}
            disabled={loading || (isUpgrade && (previewLoading || !!previewError))}
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {isUpgrade
              ? preview
                ? `Pay ${formatAmount(preview.amount_due, preview.currency)} & Upgrade`
                : 'Upgrade Now'
              : 'Schedule Downgrade'
            }
          </Button>
        </div>
      </div>
    </div>
  );
}

function PlansTab({
  user,
  billingPeriod,
  setBillingPeriod,
  upgradingPlanId,
  handlePlanChange,
  handleManageSubscription,
  hasStripeCustomer,
  showToast,
  pendingDowngrade,
  onCancelDowngrade,
  cancellingDowngrade,
  subscriptionExpiresAt,
}: any) {
  const [showNoHistoryModal, setShowNoHistoryModal] = useState(false);
  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{
    planId: string;
    planName: string;
    isUpgrade: boolean;
    effectiveDate?: string;
  } | null>(null);

  const currentTier = user?.subscription_tier || 'free';
  const currentPlanData = PLANS.find(p => p.id === currentTier) || PLANS[0];

  const memberSince = user?.created_at
    ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    : null;

  const isLifetime = currentTier === 'early_supporter';
  const isPaidPlan = currentTier !== 'free';

  const getButtonLabel = (planId: string): string => {
    const isCurrentPlan = currentTier === planId;
    if (isCurrentPlan) return 'Current Plan';
    if (planId === 'enterprise') return 'Contact Us';
    if (upgradingPlanId === planId) return 'Processing...';

    // Check if this plan has a pending downgrade scheduled
    if (pendingDowngrade?.tier === planId) return 'Downgrade Scheduled';

    if (isTierUpgrade(currentTier, planId)) return 'Upgrade';
    if (isTierDowngrade(currentTier, planId)) return 'Downgrade';
    return 'Switch';
  };

  const handleCardAction = (planId: string) => {
    if (planId === 'enterprise') {
      setShowEnterpriseModal(true);
      return;
    }

    const plan = PLANS.find(p => p.id === planId);
    if (!plan) return;

    // If this plan already has a pending downgrade, don't allow clicking again
    if (pendingDowngrade?.tier === planId) return;

    // For free users or early supporters, go straight to checkout
    if (currentTier === 'free' || currentTier === 'early_supporter') {
      handlePlanChange(planId);
      return;
    }

    // For upgrades: skip modal, go straight to Stripe Checkout
    const upgrading = isTierUpgrade(currentTier, planId);
    if (upgrading) {
      handlePlanChange(planId);
      return;
    }

    // For downgrades: show confirmation modal
    const expiresAt = user?.subscription_expires_at || subscriptionExpiresAt;
    const effectiveDate = expiresAt
      ? new Date(expiresAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
      : undefined;

    setConfirmModal({
      planId,
      planName: plan.name,
      isUpgrade: false,
      effectiveDate,
    });
  };

  const handleConfirm = () => {
    if (confirmModal) {
      handlePlanChange(confirmModal.planId);
      setConfirmModal(null);
    }
  };

  return (
    <>
    {/* Confirm Plan Change Modal */}
    <ConfirmPlanChangeModal
      isOpen={!!confirmModal}
      planId={confirmModal?.planId || ''}
      planName={confirmModal?.planName || ''}
      isUpgrade={confirmModal?.isUpgrade || false}
      effectiveDate={confirmModal?.effectiveDate}
      currentPlanName={currentPlanData.name}
      billingPeriod={billingPeriod}
      loading={!!upgradingPlanId}
      onConfirm={handleConfirm}
      onCancel={() => setConfirmModal(null)}
    />

    {/* No Payment History Modal */}
    {showNoHistoryModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50" onClick={() => setShowNoHistoryModal(false)} />
        <div className="relative bg-[var(--color-bg-primary)] rounded-xl p-6 max-w-sm w-full border border-[var(--color-border)] text-center">
          <div className="w-12 h-12 rounded-full bg-[var(--color-bg-secondary)] flex items-center justify-center mx-auto mb-4">
            <CreditCard className="h-6 w-6 text-[var(--color-text-muted)]" />
          </div>
          <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">No payment history</h3>
          <p className="mt-2 text-sm text-[var(--color-text-muted)]">
            You haven't made any payments yet. Upgrade to a paid plan to start building your payment history.
          </p>
          <button
            onClick={() => setShowNoHistoryModal(false)}
            className="mt-5 w-full py-2.5 rounded-lg text-sm font-medium bg-[var(--color-bg-secondary)] text-[var(--color-text-primary)] hover:bg-[var(--color-bg-tertiary)] cursor-pointer transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    )}

    {/* Enterprise Contact Modal */}
    {showEnterpriseModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50" onClick={() => setShowEnterpriseModal(false)} />
        <div className="relative bg-[var(--color-bg-primary)] rounded-xl p-6 max-w-md w-full border border-[var(--color-border)]">
          <button
            onClick={() => setShowEnterpriseModal(false)}
            className="absolute top-4 right-4 p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] cursor-pointer"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center">
              <Crown className="h-6 w-6 text-amber-500" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">Enterprise Plan</h3>
              <p className="text-sm text-[var(--color-text-muted)]">Custom solutions for your organization</p>
            </div>
          </div>

          <div className="space-y-2 mb-5">
            <div className="flex items-center gap-2 text-sm text-[var(--color-text-muted)]">
              <Check className="h-4 w-4 text-amber-500" />
              <span>Unlimited tracked stocks</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-[var(--color-text-muted)]">
              <Check className="h-4 w-4 text-amber-500" />
              <span>Full API access with dedicated endpoints</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-[var(--color-text-muted)]">
              <Check className="h-4 w-4 text-amber-500" />
              <span>Priority support & dedicated account manager</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-[var(--color-text-muted)]">
              <Check className="h-4 w-4 text-amber-500" />
              <span>Custom integrations & onboarding</span>
            </div>
          </div>

          <p className="text-xs text-[var(--color-text-muted)] mb-3">Get in touch to discuss your needs:</p>

          <div className="flex items-center gap-2 p-3 rounded-lg bg-[var(--color-bg-secondary)] border border-[var(--color-border)]">
            <span className="flex-1 text-sm font-medium text-[var(--color-text-primary)]">enterprise@sentimentedge.com</span>
            <button
              onClick={() => {
                navigator.clipboard.writeText('enterprise@sentimentedge.com');
                showToast('Copied to clipboard');
              }}
              className="p-2 rounded-lg hover:bg-[var(--color-bg-tertiary)] cursor-pointer transition-colors"
            >
              <Copy className="h-4 w-4 text-[var(--color-text-muted)]" />
            </button>
          </div>
        </div>
      </div>
    )}

    <div className="space-y-4">
      {/* Pending Downgrade Banner */}
      {pendingDowngrade && (
        <div className="p-4 sm:p-5 rounded-xl bg-orange-500/10 border border-orange-500/30">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-orange-600 dark:text-orange-400">
                Downgrade scheduled
              </p>
              <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                Your plan will change to <span className="font-semibold">{PLANS.find(p => p.id === pendingDowngrade.tier)?.name || pendingDowngrade.tier}</span>
                {pendingDowngrade.effective_date && (
                  <> on {new Date(pendingDowngrade.effective_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</>
                )}. You keep full access to your current plan until then.
              </p>
            </div>
            <button
              onClick={onCancelDowngrade}
              disabled={cancellingDowngrade}
              className="px-3 py-1.5 text-xs font-medium rounded-lg border border-orange-500/50 text-orange-600 dark:text-orange-400 hover:bg-orange-500/10 cursor-pointer whitespace-nowrap transition-colors flex items-center gap-1.5 self-start sm:self-auto"
            >
              {cancellingDowngrade && <Loader2 className="h-3 w-3 animate-spin" />}
              Keep Current Plan
            </button>
          </div>
        </div>
      )}

      {/* Current Plan Summary */}
      <div className="p-4 sm:p-5 rounded-xl bg-[var(--color-bg-primary)] border border-[var(--color-border)]">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          {/* Left: Plan Info */}
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-[var(--color-accent)]/10 flex items-center justify-center shrink-0">
              <Check className="h-5 w-5 text-[var(--color-accent)]" />
            </div>
            <div>
              <p className="text-xs text-[var(--color-text-muted)] mb-0.5">Current Plan</p>
              <p className="text-lg font-semibold text-[var(--color-text-primary)]">{currentPlanData.name}</p>
            </div>
          </div>

          {/* Right: Subscription Details */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            {memberSince && (
              <div>
                <p className="text-[10px] text-[var(--color-text-muted)] uppercase tracking-wide">Member since</p>
                <p className="text-[var(--color-text-primary)] font-medium">{memberSince}</p>
              </div>
            )}
            <div>
              <p className="text-[10px] text-[var(--color-text-muted)] uppercase tracking-wide">Billing</p>
              <p className="text-[var(--color-text-primary)] font-medium">
                {!isPaidPlan
                  ? 'Free forever'
                  : isLifetime
                    ? 'Lifetime access'
                    : user?.subscription_expires_at
                      ? `Renews ${new Date(user.subscription_expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                      : 'Monthly'
                }
              </p>
            </div>
            <button
              onClick={() => hasStripeCustomer ? handleManageSubscription() : setShowNoHistoryModal(true)}
              className="px-3 py-1.5 text-xs font-medium rounded-lg border border-[var(--color-border)] text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] cursor-pointer whitespace-nowrap transition-colors"
            >
              Payment history
            </button>
          </div>
        </div>
      </div>

      {/* Plan Selection Card */}
      <div className="p-4 sm:p-5 rounded-xl bg-[var(--color-bg-primary)] border border-[var(--color-border)]">
        {/* Header with Toggle */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <SectionLabel>Available Plans</SectionLabel>
          <div className="inline-flex items-center gap-1 sm:gap-3 bg-[var(--color-bg-secondary)] rounded-full p-1 sm:p-1.5 border border-[var(--color-border)] self-start sm:self-auto">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={clsx(
                'px-3 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer',
                billingPeriod === 'monthly'
                  ? 'bg-[var(--color-accent)] text-black'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
              )}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingPeriod('yearly')}
              className={clsx(
                'px-3 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer flex items-center gap-1 sm:gap-2',
                billingPeriod === 'yearly'
                  ? 'bg-[var(--color-accent)] text-black'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
              )}
            >
              Yearly
              <span className={clsx(
                'text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full',
                billingPeriod === 'yearly' ? 'bg-black/20 text-black' : 'bg-[var(--color-accent)]/20 text-[var(--color-accent)]'
              )}>
                -32%
              </span>
            </button>
          </div>
        </div>

        {/* Plan Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {PLANS.map((plan) => {
            const isCurrentPlan = currentTier === plan.id;
            const displayPrice = billingPeriod === 'yearly' && plan.yearlyPrice ? plan.yearlyPrice : plan.monthlyPrice;
            const displayPeriod = billingPeriod === 'yearly' && plan.yearlyPrice ? '/yr' : plan.period;
            const isEnterprisePlan = 'isEnterprise' in plan && plan.isEnterprise;
            const isUpgradingThis = upgradingPlanId === plan.id;
            const buttonLabel = getButtonLabel(plan.id);
            const isPendingDowngradeTarget = pendingDowngrade?.tier === plan.id;
            const isDisabled = isCurrentPlan || isUpgradingThis || isPendingDowngradeTarget || (!isEnterprisePlan && !!upgradingPlanId);

            return (
              <div
                key={plan.id}
                onClick={() => !isDisabled && handleCardAction(plan.id)}
                className={clsx(
                  'relative p-4 sm:p-5 rounded-xl transition-all flex flex-col min-h-[240px] sm:min-h-[280px]',
                  isCurrentPlan
                    ? 'border border-[var(--color-text-muted)] bg-[var(--color-bg-tertiary)]'
                    : plan.popular
                      ? 'border-2 border-[var(--color-accent)]/50 bg-[var(--color-accent)]/5 cursor-pointer hover:border-[var(--color-accent)]'
                      : isEnterprisePlan
                        ? 'border border-amber-500/50 bg-amber-500/5 cursor-pointer hover:border-amber-500'
                        : 'border border-[var(--color-border)] bg-[var(--color-bg-secondary)] cursor-pointer hover:border-[var(--color-text-muted)]'
                )}
              >
                {plan.badge && !isCurrentPlan && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-xs font-bold text-white whitespace-nowrap">
                    {plan.badge}
                  </span>
                )}
                {isCurrentPlan && (
                  <span className="absolute top-2 right-2 sm:top-3 sm:right-3 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wide bg-[var(--color-accent)] text-black">
                    Current
                  </span>
                )}

                <div className="flex items-center gap-2 mb-3">
                  {isEnterprisePlan && <Crown className="h-4 w-4 text-amber-500" />}
                  <p className="text-base font-semibold text-[var(--color-text-primary)]">{plan.name}</p>
                </div>

                <div className="mb-4">
                  <div>
                    <span className="text-2xl sm:text-3xl font-bold text-[var(--color-text-primary)]">{displayPrice}</span>
                    <span className="text-sm text-[var(--color-text-muted)]">{displayPeriod}</span>
                  </div>
                  {billingPeriod === 'yearly' && plan.yearlyPrice && plan.monthlyPrice && (
                    <p className="text-xs text-[var(--color-accent)] font-medium mt-1">
                      Save ${parseInt(plan.monthlyPrice.replace('$', '')) * 12 - parseInt(plan.yearlyPrice.replace('$', ''))}/year
                    </p>
                  )}
                </div>

                <ul className="space-y-2 flex-grow mb-4">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-[var(--color-text-muted)]">
                      <Check className="w-4 h-4 text-[var(--color-accent)] shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isDisabled) handleCardAction(plan.id);
                  }}
                  disabled={isDisabled}
                  className={clsx(
                    'w-full py-2.5 rounded-lg text-sm font-medium transition-all mt-auto flex items-center justify-center gap-2',
                    isCurrentPlan
                      ? 'bg-[var(--color-bg-tertiary)] text-[var(--color-text-muted)] cursor-default'
                      : isEnterprisePlan
                        ? 'bg-amber-500 text-black hover:opacity-90 cursor-pointer'
                        : 'border border-[var(--color-border)] text-[var(--color-text-primary)] hover:border-[var(--color-text-muted)] hover:bg-[var(--color-bg-tertiary)] cursor-pointer'
                  )}
                >
                  {isUpgradingThis && <Loader2 className="h-4 w-4 animate-spin" />}
                  {buttonLabel}
                </button>
              </div>
            );
          })}
        </div>
      </div>

    </div>
    </>
  );
}

// ============================================================================
// TAB: SECURITY
// ============================================================================

function SecurityTab({ showToast, onDeleteAccount }: { showToast: (msg: string, type?: 'success' | 'error') => void; onDeleteAccount: () => void }) {
  return (
    <div className="p-4 sm:p-5 rounded-xl bg-[var(--color-bg-primary)] border border-[var(--color-border)]">
      <SectionLabel>Security</SectionLabel>
      {/* Password */}
      <div
        onClick={() => showToast('Coming soon', 'error')}
        className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 -mx-1 rounded-lg hover:bg-[var(--color-bg-secondary)] cursor-pointer transition-colors"
      >
        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-[var(--color-bg-secondary)] flex items-center justify-center shrink-0">
          <Key className="h-4 w-4 sm:h-5 sm:w-5 text-[var(--color-text-muted)]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[var(--color-text-primary)]">Password</p>
          <p className="text-xs text-[var(--color-text-muted)] truncate">Change your account password</p>
        </div>
        <span className="text-[var(--color-text-muted)] shrink-0">→</span>
      </div>

      <div className="border-t border-[var(--color-border)] my-1" />

      {/* 2FA */}
      <div
        onClick={() => showToast('Coming soon', 'error')}
        className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 -mx-1 rounded-lg hover:bg-[var(--color-bg-secondary)] cursor-pointer transition-colors"
      >
        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-[var(--color-bg-secondary)] flex items-center justify-center shrink-0">
          <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-[var(--color-text-muted)]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[var(--color-text-primary)]">Two-factor authentication</p>
          <p className="text-xs text-[var(--color-text-muted)] truncate">Add an extra layer of security</p>
        </div>
        <span className="px-2 py-1 text-xs rounded-full bg-[var(--color-bg-secondary)] text-[var(--color-text-muted)] shrink-0">Off</span>
      </div>

      <div className="border-t border-[var(--color-border)] my-1" />

      {/* Delete Account */}
      <div
        onClick={onDeleteAccount}
        className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 -mx-1 rounded-lg hover:bg-red-500/5 cursor-pointer transition-colors group"
      >
        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-[var(--color-bg-secondary)] group-hover:bg-red-500/10 flex items-center justify-center transition-colors shrink-0">
          <Trash2 className="h-4 w-4 sm:h-5 sm:w-5 text-[var(--color-text-muted)] group-hover:text-red-500 transition-colors" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[var(--color-text-primary)] group-hover:text-red-500 transition-colors">Delete account</p>
          <p className="text-xs text-[var(--color-text-muted)] truncate">Permanently remove your account and data</p>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// TAB: API KEYS
// ============================================================================

function ApiKeysTab({
  apiKeys,
  loadingKeys,
  keyError,
  newKeyName,
  setNewKeyName,
  newKeyValue,
  showNewKey,
  setShowNewKey,
  setNewKeyValue,
  handleCreateKey,
  handleRevokeKey,
  showToast,
}: any) {
  const copyKey = (text: string) => {
    navigator.clipboard.writeText(text);
    showToast('Copied to clipboard');
  };

  return (
    <div className="space-y-4">
      {/* Create API Key Card */}
      <div className="p-4 sm:p-5 rounded-xl bg-[var(--color-bg-primary)] border border-[var(--color-border)]">
        <SectionLabel>Create API Key</SectionLabel>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            placeholder="Key name (e.g., Production)"
            className="flex-1 px-3 py-2.5 rounded-lg text-sm bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:ring-1 focus:ring-[var(--color-accent)]"
          />
          <Button size="sm" onClick={handleCreateKey} className="sm:w-auto w-full">Create</Button>
        </div>
        {keyError && <p className="mt-2 text-sm text-red-500">{keyError}</p>}
      </div>

      {newKeyValue && showNewKey && (
        <div className="p-4 sm:p-5 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
          <p className="text-sm font-medium text-emerald-600 mb-3">Key created — copy it now, it won't be shown again:</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <code className="flex-1 p-3 rounded-lg bg-[var(--color-bg-tertiary)] text-xs font-mono overflow-x-auto text-[var(--color-text-primary)] break-all">
              {newKeyValue}
            </code>
            <Button variant="secondary" size="sm" onClick={() => copyKey(newKeyValue)} className="sm:w-auto w-full shrink-0">
              <Copy className="h-3.5 w-3.5 mr-1 sm:mr-0" />
              <span className="sm:hidden">Copy</span>
            </Button>
          </div>
          <button
            onClick={() => { setShowNewKey(false); setNewKeyValue(null); }}
            className="mt-3 text-xs text-emerald-600 hover:underline cursor-pointer"
          >
            I've saved it
          </button>
        </div>
      )}

      {/* Your Keys Card */}
      <div className="p-4 sm:p-5 rounded-xl bg-[var(--color-bg-primary)] border border-[var(--color-border)]">
        <SectionLabel>Your Keys</SectionLabel>
        {loadingKeys ? (
          <div className="flex items-center gap-2 text-sm text-[var(--color-text-muted)]">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading...
          </div>
        ) : apiKeys.length === 0 ? (
          <p className="text-sm text-[var(--color-text-muted)]">No API keys yet</p>
        ) : (
          <div className="space-y-2">
            {apiKeys.map((key: ApiKeyData) => (
              <div key={key.id} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-[var(--color-bg-secondary)] border border-[var(--color-border)]">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-[var(--color-text-primary)] truncate">{key.name}</p>
                  <p className="text-xs text-[var(--color-text-muted)] truncate">
                    {key.prefix}••• · {key.total_requests.toLocaleString()} requests
                  </p>
                </div>
                <button
                  onClick={() => handleRevokeKey(key.id)}
                  className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg cursor-pointer transition-colors shrink-0"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function Settings() {
  const { theme, setTheme } = useThemeStore();
  const { user, updateSubscription } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();

  const isPro = user?.subscription_tier === 'pro' || user?.subscription_tier === 'enterprise' || user?.subscription_tier === 'early_supporter';
  const isEnterprise = user?.subscription_tier === 'enterprise';
  const hasStripeCustomer = !!user?.stripe_customer_id;

  const [activeTab, setActiveTab] = useState<TabId>('general');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [notifications, setNotifications] = useState({ email: true, push: false, signals: true, weekly: true });
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [upgradingPlanId, setUpgradingPlanId] = useState<string | null>(null);
  const [pendingDowngrade, setPendingDowngrade] = useState<{ tier: string; effective_date: string | null } | null>(
    user?.pending_downgrade_tier
      ? { tier: user.pending_downgrade_tier, effective_date: user.pending_downgrade_at || null }
      : null
  );
  const [cancellingDowngrade, setCancellingDowngrade] = useState(false);
  const [subscriptionExpiresAt, setSubscriptionExpiresAt] = useState<string | null>(
    user?.subscription_expires_at || null
  );
  const [upgradeSuccess, setUpgradeSuccess] = useState<{
    tier: string;
    planName: string;
    amountCharged: number;
    nextBillingAmount: number;
    billingInterval: string;
    nextBillingDate: string | null;
  } | null>(null);

  const [apiKeys, setApiKeys] = useState<ApiKeyData[]>([]);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyValue, setNewKeyValue] = useState<string | null>(null);
  const [showNewKey, setShowNewKey] = useState(false);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [keyError, setKeyError] = useState<string | null>(null);

  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
  }, []);

  // Fetch subscription status including pending downgrade info
  useEffect(() => {
    if (user) {
      import('../utils/stripe').then(({ getSubscriptionStatus }) => {
        getSubscriptionStatus()
          .then((status) => {
            if (status.pending_downgrade) {
              setPendingDowngrade(status.pending_downgrade);
            } else {
              setPendingDowngrade(null);
            }
            if (status.subscription_expires_at) {
              setSubscriptionExpiresAt(status.subscription_expires_at);
            }
          })
          .catch(() => {
            // Silently fail - not critical
          });
      });
    }
  }, [user]);

  // Handle return from Stripe Checkout (new subscription or upgrade)
  useEffect(() => {
    const handleCheckoutReturn = async () => {
      if (searchParams.get('subscription') === 'success') {
        searchParams.delete('subscription');
        setSearchParams(searchParams, { replace: true });
        window.location.reload();
        return;
      }

      // Handle upgrade checkout return
      const upgradeStatus = searchParams.get('upgrade');
      const sessionId = searchParams.get('session_id');
      if (upgradeStatus === 'success' && sessionId) {
        try {
          const result = await completeUpgrade(sessionId);
          updateSubscription(result.new_tier as SubscriptionTierType, 'active');
          const tierConfig = SUBSCRIPTION_TIERS[result.new_tier as keyof typeof SUBSCRIPTION_TIERS];
          const planName = tierConfig?.name || result.new_tier.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase());
          setUpgradeSuccess({
            tier: result.new_tier,
            planName,
            amountCharged: result.amount_charged || 0,
            nextBillingAmount: result.next_billing_amount || 0,
            billingInterval: result.billing_interval || 'month',
            nextBillingDate: result.next_billing_date || null,
          });
        } catch (e: any) {
          console.error('Complete upgrade error:', e);
          showToast(e?.message || 'Failed to complete upgrade', 'error');
        }
        // Clean URL params
        searchParams.delete('upgrade');
        searchParams.delete('session_id');
        searchParams.delete('tier');
        setSearchParams(searchParams, { replace: true });
      }
    };

    handleCheckoutReturn();
  }, [searchParams, setSearchParams, updateSubscription, showToast]);

  useEffect(() => {
    if (isEnterprise && activeTab === 'api') {
      setLoadingKeys(true);
      getApiKeys()
        .then((data) => setApiKeys(data.keys || []))
        .catch(() => setKeyError('Failed to load API keys'))
        .finally(() => setLoadingKeys(false));
    }
  }, [isEnterprise, activeTab]);

  const handlePlanChange = async (planId: string) => {
    const currentTier = user?.subscription_tier || 'free';
    const isUpgrading = isTierUpgrade(currentTier, planId);
    setUpgradingPlanId(planId);
    try {
      if (planId === 'free') {
        showToast('You are already on the Free plan', 'error');
        setUpgradingPlanId(null);
        return;
      }

      // FREE users → Stripe Checkout (need to enter card info for first time)
      if (currentTier === 'free') {
        let priceId: string;
        if (planId === 'early_supporter') {
          priceId = SUBSCRIPTION_TIERS.early_supporter.priceId;
        } else {
          const tier = SUBSCRIPTION_TIERS[planId as 'starter' | 'pro'];
          if (!tier || !('priceIds' in tier)) throw new Error('Invalid plan');
          priceId = billingPeriod === 'yearly' ? tier.priceIds.yearly : tier.priceIds.monthly;
        }
        if (!priceId) {
          showToast('This plan is not available yet. Please try a different plan or billing period.', 'error');
          setUpgradingPlanId(null);
          return;
        }
        await redirectToCheckout(priceId, user?.email);
        return;
      }

      // EXISTING SUBSCRIBERS
      if (isUpgrading) {
        // UPGRADE: Redirect to Stripe Checkout for prorated payment
        const checkoutUrl = await createUpgradeCheckout(planId, billingPeriod);
        if (checkoutUrl === 'free') {
          // No charge needed - upgrade was free
          updateSubscription(planId as SubscriptionTierType, 'active');
          showToast('Upgraded at no additional cost!');
          setActiveTab('plans');
          setUpgradingPlanId(null);
        } else {
          // Redirect to Stripe Checkout
          window.location.href = checkoutUrl;
        }
        return;
      }

      // DOWNGRADE: Schedule at end of billing period via API
      const result = await changeSubscription(planId, billingPeriod);

      if (result.action === 'downgrade_scheduled') {
        setPendingDowngrade({
          tier: result.new_tier,
          effective_date: result.effective_date || null,
        });
        showToast(result.message);
      }
      setUpgradingPlanId(null);
    } catch (e: any) {
      console.error('Plan change error:', e);
      showToast(e?.message || 'Failed to change plan', 'error');
      setUpgradingPlanId(null);
    }
  };

  const handleCancelDowngrade = async () => {
    setCancellingDowngrade(true);
    try {
      const result = await cancelPendingDowngrade();
      setPendingDowngrade(null);
      showToast(result.message);
    } catch (e: any) {
      console.error('Cancel downgrade error:', e);
      showToast(e?.message || 'Failed to cancel downgrade', 'error');
    } finally {
      setCancellingDowngrade(false);
    }
  };

  const handleManageSubscription = async () => {
    try { await redirectToPortal(); } catch (e) { console.error(e); }
  };

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) { setKeyError('Enter a key name'); return; }
    setKeyError(null);
    try {
      const res = await createApiKey(newKeyName.trim());
      setNewKeyValue(res.key);
      setShowNewKey(true);
      setApiKeys((prev) => [{ id: res.id, name: res.name, prefix: res.prefix, created_at: res.created_at, last_used: null, total_requests: 0, is_active: true }, ...prev]);
      setNewKeyName('');
      showToast('API key created');
    } catch (e: any) {
      setKeyError(e?.response?.data?.detail || 'Failed to create key');
    }
  };

  const handleRevokeKey = async (keyId: number) => {
    try {
      await revokeApiKey(keyId);
      setApiKeys((prev) => prev.filter((k) => k.id !== keyId));
      showToast('Key revoked');
    } catch (e: any) {
      setKeyError(e?.response?.data?.detail || 'Failed to revoke key');
    }
  };

  const visibleTabs = TABS.filter((t) => !t.enterpriseOnly || isEnterprise);

  return (
    <div className="w-full">
      {/* Upgrade Success Modal */}
      {upgradeSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative bg-[var(--color-bg-primary)] rounded-2xl p-8 max-w-md w-full border border-[var(--color-border)] text-center shadow-2xl">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="h-9 w-9 text-emerald-400" />
            </div>
            <h2 className="text-2xl font-bold text-[var(--color-text-primary)]">
              Upgrade Successful!
            </h2>
            <p className="mt-2 text-[var(--color-text-muted)]">
              You're now on the <span className="text-emerald-400 font-semibold">{upgradeSuccess.planName}</span> plan.
            </p>
            <div className="mt-5 bg-[var(--color-bg-secondary)] rounded-xl p-4 text-left space-y-4">
              {/* Billing summary */}
              <div className="space-y-2">
                {upgradeSuccess.amountCharged > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-[var(--color-text-muted)]">Charged today</span>
                    <span className="text-[var(--color-text-primary)] font-medium">${(upgradeSuccess.amountCharged / 100).toFixed(2)}</span>
                  </div>
                )}
                {upgradeSuccess.nextBillingAmount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-[var(--color-text-muted)]">Next billing</span>
                    <span className="text-[var(--color-text-primary)] font-medium">${(upgradeSuccess.nextBillingAmount / 100).toFixed(2)}/{upgradeSuccess.billingInterval === 'year' ? 'yr' : 'mo'}</span>
                  </div>
                )}
                {upgradeSuccess.nextBillingDate && (
                  <div className="flex justify-between text-sm">
                    <span className="text-[var(--color-text-muted)]">Renews on</span>
                    <span className="text-[var(--color-text-primary)] font-medium">{upgradeSuccess.nextBillingDate}</span>
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="border-t border-[var(--color-border)]" />

              {/* Features */}
              <div>
                <p className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wide mb-3">What you now have access to</p>
                <ul className="space-y-2">
                  {PLANS.find(p => p.id === upgradeSuccess.tier)?.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-[var(--color-text-primary)]">
                      <Check className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="mt-6 flex flex-col gap-3">
              <Button
                onClick={() => {
                  setUpgradeSuccess(null);
                  window.location.href = '/';
                }}
                className="w-full py-3 text-base font-semibold"
              >
                Go to Dashboard
              </Button>
              <button
                onClick={() => setUpgradeSuccess(null)}
                className="py-2 text-base text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-[var(--color-text-primary)]">Settings</h1>
        {!isPro && (
          <Button
            size="sm"
            onClick={() => handlePlanChange('early_supporter')}
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 border-0 cursor-pointer"
          >
            <Crown className="h-4 w-4 mr-1.5" />
            <span className="hidden sm:inline">Upgrade</span>
            <span className="sm:hidden">Pro</span>
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[var(--color-border)] mb-4 sm:mb-6 overflow-x-auto scrollbar-hide">
        {visibleTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={clsx(
              'flex items-center gap-2 px-3 sm:px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer -mb-px shrink-0',
              activeTab === tab.id
                ? 'text-[var(--color-accent)] border-b-2 border-[var(--color-accent)]'
                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
            )}
          >
            <tab.icon className="h-4 w-4" />
            <span className="hidden sm:inline">{tab.label}</span>
            <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'general' && (
          <GeneralTab
            user={user}
            theme={theme}
            setTheme={setTheme}
            notifications={notifications}
            setNotifications={setNotifications}
            isPro={isPro}
            showToast={showToast}
          />
        )}

        {activeTab === 'plans' && (
          <PlansTab
            user={user}
            billingPeriod={billingPeriod}
            setBillingPeriod={setBillingPeriod}
            upgradingPlanId={upgradingPlanId}
            handlePlanChange={handlePlanChange}
            handleManageSubscription={handleManageSubscription}
            hasStripeCustomer={hasStripeCustomer}
            showToast={showToast}
            pendingDowngrade={pendingDowngrade}
            onCancelDowngrade={handleCancelDowngrade}
            cancellingDowngrade={cancellingDowngrade}
            subscriptionExpiresAt={subscriptionExpiresAt}
          />
        )}

        {activeTab === 'security' && (
          <SecurityTab showToast={showToast} onDeleteAccount={() => setShowDeleteModal(true)} />
        )}

        {activeTab === 'api' && isEnterprise && (
          <ApiKeysTab
            apiKeys={apiKeys}
            loadingKeys={loadingKeys}
            keyError={keyError}
            newKeyName={newKeyName}
            setNewKeyName={setNewKeyName}
            newKeyValue={newKeyValue}
            showNewKey={showNewKey}
            setShowNewKey={setShowNewKey}
            setNewKeyValue={setNewKeyValue}
            handleCreateKey={handleCreateKey}
            handleRevokeKey={handleRevokeKey}
            showToast={showToast}
          />
        )}
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <DeleteAccountModal
        isOpen={showDeleteModal}
        onConfirm={() => { setShowDeleteModal(false); showToast('Coming soon', 'error'); }}
        onCancel={() => setShowDeleteModal(false)}
      />
    </div>
  );
}

// Export for use in SettingsPanel slide-out
export const SettingsContent = Settings;
